import { TourPlayback } from "@common/schemas";
import { observer } from "mobx-react-lite";
import { PlayCircle, PlayCircleVariant } from "./PlayCircle";
import { useHover } from "usehooks-ts";
import { useEffect, useRef } from "react";
import { FlowState, TourPlaybackManager } from "@/lib/tour/tourPlayback";
import { FlowKey } from "@/lib/tour/tourPlayback/flow";
import { MousePointerClickIcon } from "lucide-react";
import { useFloating, autoUpdate, offset, flip, shift, arrow, FloatingPortal } from "@floating-ui/react";

export interface TourFlowVisualizerProps {
  tourPlaybackManager?: TourPlaybackManager;
  tourPlayback: TourPlayback | null;
  flowKey: FlowKey | null;
  activeFlowKey: FlowKey | null;
  flowState: FlowState | null;
  isRunning: boolean;
  advanced?: boolean;
  onStartTourFromStep?: (stepId: string) => void;
  onStepClick?: (stepId: string) => void;
  selectedStepId?: string;
  isStepOnTrack?: (stepId: string) => boolean;
  isStepTargetElementFound?: (stepId: string) => boolean;
  allowStepEditing?: boolean;
  onSelectorPickClick?: (stepId: string) => void;
}

export const TourPlaybackVisualizer = observer(
  ({
    tourPlayback,
    tourPlaybackManager,
    onStartTourFromStep,
    onStepClick,
    selectedStepId,
    activeFlowKey,
    isRunning,
    flowKey,
    flowState,
    isStepOnTrack,
    isStepTargetElementFound,
    allowStepEditing,
    onSelectorPickClick,
  }: TourFlowVisualizerProps) => {
    const currentSectionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (currentSectionRef.current) {
        currentSectionRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, [flowState?.currentSectionIndex]);

    if (!tourPlayback) {
      return null;
    }

    const isTourPlaying = isRunning;

    const isStepActive = (sectionIndex: number, stepIndex: number) => {
      if (!isTourPlaying || !flowState) return false;
      return flowState.currentSectionIndex === sectionIndex && flowState.currentStepIndex === stepIndex;
    };

    const isStepSelected = (stepId: string) => {
      return selectedStepId === stepId;
    };

    const isStepFailed = (stepId: string, sectionIndex: number, stepIndex: number) => {
      return isStepActive(sectionIndex, stepIndex) && isTourFailed && isStepOnTrack && !isStepOnTrack(stepId);
    };

    const playButtonVariant = (stepId: string, sectionIndex: number, stepIndex: number): PlayCircleVariant => {
      if (!isTourPlaying) return "idle";
      if (isStepFailed(stepId, sectionIndex, stepIndex)) return "failed";
      if (isStepActive(sectionIndex, stepIndex)) return "playing";

      return "idle";
    };

    const sections = isRunning && flowKey ? tourPlaybackManager?.flows[flowKey]?.sections : tourPlayback?.sections;

    const isTourFlow = flowKey === "tour";
    const isTourFailed = isTourFlow && activeFlowKey !== "tour";

    return sections?.map((section, sectionIndex) => {
      const sectionRef = sectionIndex === flowState?.currentSectionIndex ? currentSectionRef : undefined;
      return (
        <>
          <div>
            <div
              key={`section-${sectionIndex}`}
              ref={sectionRef}
              style={{
                border: "1px solid rgba(213, 213, 213, 1)",
                padding: "8px",
                marginTop: sectionIndex > 0 ? "8px" : "0",
                borderRadius: "8px",
              }}
            >
              <>
                {section.steps?.map((step, stepIndex) => {
                  const stepOrder = sections.flatMap((section) => section.steps).findIndex((s) => s.id === step.id);

                  return (
                    <StepWrapper
                      key={`step-${sectionIndex}-${stepIndex}`}
                      step={step}
                      sectionIndex={sectionIndex}
                      stepIndex={stepIndex}
                      stepOrder={stepOrder}
                      isStepActive={isStepActive}
                      isStepSelected={isStepSelected}
                      isStepFailed={isStepFailed}
                      isStepTargetElementFound={isStepTargetElementFound}
                      onStepClick={onStepClick}
                      allowStepEditing={allowStepEditing}
                      onSelectorPickClick={onSelectorPickClick}
                      onStartTourFromStep={onStartTourFromStep}
                      playButtonVariant={playButtonVariant}
                    />
                  );
                })}
              </>
            </div>
          </div>
        </>
      );
    });
  }
);

interface StepWrapperProps {
  step: any;
  sectionIndex: number;
  stepIndex: number;
  stepOrder: number;
  isStepActive: (sectionIndex: number, stepIndex: number) => boolean;
  isStepSelected: (stepId: string) => boolean;
  isStepFailed: (stepId: string, sectionIndex: number, stepIndex: number) => boolean | undefined;
  onStepClick?: (stepId: string) => void;
  allowStepEditing?: boolean;
  onSelectorPickClick?: (stepId: string) => void;
  onStartTourFromStep?: (stepId: string) => void;
  playButtonVariant: (stepId: string, sectionIndex: number, stepIndex: number) => PlayCircleVariant;
  isStepTargetElementFound?: (stepId: string) => boolean;
}

const StepWrapper = ({
  step,
  sectionIndex,
  stepIndex,
  stepOrder,
  isStepActive,
  isStepSelected,
  isStepFailed,
  isStepTargetElementFound,
  onStepClick,
  allowStepEditing,
  onSelectorPickClick,
  onStartTourFromStep,
  playButtonVariant,
}: StepWrapperProps) => {
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context } = useFloating({
    placement: "left",
    middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const failed = isStepFailed(step.id, sectionIndex, stepIndex);

  return (
    <>
      <div
        key={`step-${sectionIndex}-${stepIndex}`}
        ref={refs.setReference}
        className={`pyne-debugger-v2__tour-playback-visualizer__step ${isStepActive(sectionIndex, stepIndex) ? "active" : ""} ${isStepSelected(step.id) ? "selected" : ""} ${failed ? "failed" : ""}`}
        style={{
          width: "100%",
          padding: "4px 8px",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={() => onStepClick?.(step.id)}
      >
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "rgba(156, 163, 175, 1)",
          }}
        >
          <span
            className="pyne-debugger-v2__tour-playback-visualizer__step-title"
            style={{
              fontWeight: "600",
              fontSize: "12px",
              lineHeight: "20px",
            }}
          >
            {stepOrder + 1}. {step.attributes.type} -{" "}
          </span>
          <span style={{ color: "rgba(156, 163, 175, 1)", fontSize: "12px", lineHeight: "20px" }} title={step.text}>
            {" "}
            {step.text}{" "}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {allowStepEditing && "targetElementSelector" in step.attributes && (
            <div>
              <StepEditButton onClick={() => onSelectorPickClick?.(step.id)} />
            </div>
          )}
          <StepPlayButton
            onClick={() => onStartTourFromStep?.(step.id)}
            variant={playButtonVariant(step.id, sectionIndex, stepIndex)}
          />
        </div>
      </div>
      {failed && allowStepEditing && !isStepTargetElementFound?.(step.id) && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              background: "white",
              color: "black",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              width: "auto",
              height: "auto",
              zIndex: 2147483647,
              maxWidth: "220px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              border: "1px solid #e5e7eb",
            }}
          >
            This step failed due to selector issue. Try picking a new element by clicking{" "}
            <MousePointerClickIcon
              style={{
                display: "inline-block",
                verticalAlign: "middle",
                width: "12px",
                height: "12px",
                marginLeft: "2px",
                marginRight: "2px",
              }}
            />{" "}
            button.
            <div
              ref={arrowRef}
              style={(() => {
                const arrowContentSize = 6;
                const borderWidth = 1;
                const primaryPlacement = context.placement.split("-")[0] as "top" | "right" | "bottom" | "left";
                const staticSideKey = {
                  top: "bottom",
                  right: "left",
                  bottom: "top",
                  left: "right",
                }[primaryPlacement];

                const styles: React.CSSProperties = {
                  position: "absolute",
                  left: context.middlewareData.arrow?.x != null ? `${context.middlewareData.arrow.x}px` : undefined,
                  top: context.middlewareData.arrow?.y != null ? `${context.middlewareData.arrow.y}px` : undefined,
                  width: `${arrowContentSize}px`,
                  height: `${arrowContentSize}px`,
                  background: "white",
                  transform: "rotate(45deg)",
                  borderWidth: `${borderWidth}px`,
                  borderStyle: "solid",
                  borderColor: "white",
                  borderTopColor: "#e5e7eb",
                  borderRightColor: "#e5e7eb",
                };

                if (staticSideKey) {
                  styles[staticSideKey as "left" | "right" | "top" | "bottom"] =
                    `${-(arrowContentSize / 2 + borderWidth)}px`;
                }

                return styles;
              })()}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

const StepPlayButton = ({ variant, onClick }: { variant?: PlayCircleVariant; onClick: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useHover(ref);

  return (
    <div
      ref={ref}
      style={{
        cursor: "pointer",
        background: "none",
        border: "none",
        padding: 0,
        display: "flex",
        alignItems: "center",
      }}
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <PlayCircle variant={isHovering ? "hover" : variant} />
    </div>
  );
};

const StepEditButton = ({ onClick }: { onClick: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useHover(ref);

  return (
    <div
      ref={ref}
      style={{
        cursor: "pointer",
        background: isHovering ? "rgba(213, 213, 213, 0.5)" : "transparent",
        border: "none",
        padding: "4px",
        display: "flex",
        alignItems: "center",
        borderRadius: "50%",
      }}
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <MousePointerClickIcon style={{ width: 12, height: 12 }} />
    </div>
  );
};
